import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { STEWARD_NODES } from "@/data/content";

export default function StewardshipIntelligence() {
  const cx = 250, cy = 250, r = 180;
  const nodes = STEWARD_NODES.map((label, i) => {
    const angle = (i / STEWARD_NODES.length) * Math.PI * 2 - Math.PI / 2;
    return { label, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });

  return (
    <section className="bg-charcoal text-cream py-24 md:py-40 overflow-hidden" data-testid="stewardship-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <Reveal>
          <p className="overline text-ochre mb-6">Stewardship Intelligence</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1]">
            The oldest intelligence isn't stored in books.
            <span className="block text-ochre mt-3">It lives in relationship.</span>
          </h2>
          <p className="mt-8 text-cream/70 text-lg font-light leading-relaxed max-w-lg">
            The Academy teaches stewardship as a practical form of intelligence — one that emerges
            through enduring relationships between people, place, biodiversity and future generations.
          </p>
          <p className="mt-5 text-cream/70 text-lg font-light leading-relaxed max-w-lg">
            It is knowledge that cannot be extracted, only cultivated. This is the defining idea at the
            heart of SeedKeepers Academy.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <svg viewBox="0 0 500 500" className="w-full max-w-[520px] mx-auto" data-testid="stewardship-diagram">
            {nodes.map((n, i) => (
              <motion.line
                key={`l${i}`}
                x1={cx} y1={cy} x2={n.x} y2={n.y}
                stroke="rgba(201,106,62,0.4)" strokeWidth="1"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: 0.3 + i * 0.12 }}
              />
            ))}
            {nodes.map((a, i) =>
              nodes.map((b, j) =>
                j > i ? (
                  <motion.line
                    key={`c${i}-${j}`}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke="rgba(247,244,235,0.07)" strokeWidth="0.75"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 1 + (i + j) * 0.05 }}
                  />
                ) : null
              )
            )}
            <motion.circle
              cx={cx} cy={cy} r="46" fill="#2A4D3A" stroke="#C96A3E" strokeWidth="1.5"
              initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "backOut" }}
            />
            <text x={cx} y={cy - 4} textAnchor="middle" className="fill-cream font-serif" fontSize="17">Steward</text>
            <text x={cx} y={cy + 15} textAnchor="middle" className="fill-cream/60" fontSize="10">-ship</text>

            {nodes.map((n, i) => (
              <motion.g
                key={`n${i}`}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.12, ease: "backOut" }}
              >
                <circle cx={n.x} cy={n.y} r="7" fill="#C96A3E" />
                <text
                  x={n.x}
                  y={n.y > cy ? n.y + 24 : n.y - 16}
                  textAnchor="middle"
                  className="fill-cream/90 font-sans"
                  fontSize="13"
                >
                  {n.label}
                </text>
              </motion.g>
            ))}
          </svg>
        </Reveal>
      </div>
    </section>
  );
}
